import React from 'react';
import { Card, CardHeader, CardContent, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Clock } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { formatNotificationTitle, formatNotificationDetails } from '@/lib/notificationFormat';
import { useLocation } from 'wouter';

interface Props {
  reminders?: any[];
  dismiss?: (id: number) => void;
  className?: string;
}

export default function RemindersCard({ reminders = [], dismiss, className = '' }: Props) {
  const [, setLocation] = useLocation();

  return (
    <Card className={`${className} rounded-2xl shadow-md border border-yellow-200 bg-yellow-50 p-4 sm:p-5 min-w-0 relative overflow-hidden`}>
      <CardHeader className="bg-transparent p-0">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <CardTitle className="tile-title text-lg md:text-xl font-extrabold text-yellow-900 leading-tight flex items-center">
              <Clock className="w-5 h-5 mr-2 text-yellow-700 flex-shrink-0" />
              <span className="truncate">Reminders</span>
              <Badge className="ml-2 bg-yellow-100 text-yellow-800">{reminders.length}</Badge>
            </CardTitle>
            <p className="text-sm text-yellow-800/80 mt-1">Upcoming and overdue items</p>
          </div>
        </div>
      </CardHeader>
      <CardContent className="pt-4 p-0">
        <div className="mt-4 space-y-3 max-h-[60vh] sm:max-h-[380px] overflow-auto pr-1">
          {reminders.map((reminder: any) => (
            <div
              key={reminder.id}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  if (dismiss) dismiss(reminder.id);
                  if (reminder.link || reminder.audit_url) setLocation(reminder.link || reminder.audit_url);
                }
              }}
              onClick={() => {
                if (dismiss) dismiss(reminder.id);
                if (reminder.link || reminder.audit_url) setLocation(reminder.link || reminder.audit_url);
              }}
              className="w-full text-left rounded-xl border border-yellow-200 bg-white dark:bg-slate-800 hover:bg-yellow-50 transition-colors px-3 py-3 flex items-center justify-between gap-3 cursor-pointer"
            >
              <div className="flex items-center gap-2 min-w-0">
                <Clock className="h-4 w-4 shrink-0 text-yellow-700" />
                <span className="truncate font-medium text-yellow-900">{formatNotificationTitle(reminder)}</span>
              </div>
              <div className="flex items-center gap-3">
                <div className="text-sm text-yellow-800/80 shrink-0">{formatNotificationDetails(reminder)}</div>
                <button
                  className="inline-flex items-center text-sm text-yellow-800/80 hover:text-yellow-900 py-2 px-3"
                  onClick={(e) => {
                    e.stopPropagation();
                    if (dismiss) dismiss(reminder.id);
                  }}
                >
                  Dismiss
                </button>
              </div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}
